import {
  Content,
  Item,
  ItemText,
  Portal,
  type SelectTriggerProps as PrimitiveSelectTriggerProps,
  type SelectProps as RawSelectProps,
  Root,
  ScrollDownButton,
  ScrollUpButton,
  type SelectContentProps,
  Icon as SelectIcon,
  type SelectItemProps,
  type SelectValueProps,
  type SelectViewportProps,
  Trigger,
  Value,
  Viewport,
} from '@radix-ui/react-select';
import { cva, type VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import { isValidElement, ReactNode, useRef, useState } from 'react';
import { isNullish } from 'remeda';
import { Icon, IconName } from 'ui-icons';
import { ExpandableGroupTagLine } from '../ExpandableGroupTagLine/ExpandableGroupTagLine';
import { MenuCommand } from '../MenuCommand/MenuCommand';
import Tag from '../Tag/Tag';
import { cn } from '../utils';

function SelectContent({ children, className, ...props }: React.PropsWithChildren<SelectContentProps>) {
  return (
    <Portal>
      <Content
        className={clsx('bg-surface-card border-grey-border z-50 mt-sm rounded-sm border shadow-md', className)}
        position="popper"
        {...props}
      >
        <ScrollUpButton className="flex h-6 justify-center">
          <Icon icon="arrow-2-up" />
        </ScrollUpButton>
        {children}
        <ScrollDownButton className="flex h-6 justify-center">
          <Icon icon="arrow-2-down" />
        </ScrollDownButton>
      </Content>
    </Portal>
  );
}

function SelectViewport({ children, className, ...props }: React.PropsWithChildren<SelectViewportProps>) {
  return (
    <Viewport className={clsx('flex flex-col gap-sm p-sm', className)} {...props}>
      {children}
    </Viewport>
  );
}

export const selectTrigger = cva(
  'text-s text-grey-primary flex min-h-10 min-w-10 items-center justify-between border font-medium outline-hidden radix-state-open:border-purple-primary radix-state-open:text-purple-primary radix-disabled:border-grey-border radix-disabled:bg-grey-background radix-disabled:text-grey-secondary radix-placeholder:text-grey-disabled radix-placeholder:radix-state-open:text-grey-disabled',
  {
    variants: {
      backgroundColor: {
        enabled: 'bg-surface-card',
        disabled: 'bg-grey-background-light',
      },
      border: {
        square: 'gap-sm rounded-sm p-sm',
        rounded: 'rounded-full p-sm',
      },
      borderColor: {
        'greyfigma-90': 'border-grey-border focus:border-purple-primary',
        'redfigma-47': 'border-red-primary focus:border-purple-primary',
        'redfigma-87': 'border-red-secondary focus:border-purple-primary',
      },
    },
  },
);

export interface SelectTriggerProps extends PrimitiveSelectTriggerProps, VariantProps<typeof selectTrigger> {}

const SelectTrigger = function SelectTrigger({
  ref,
  children,
  className,
  border = 'square',
  borderColor = 'greyfigma-90',
  disabled,
  ...props
}: SelectTriggerProps & { ref?: React.Ref<HTMLButtonElement> }) {
  return (
    <Trigger
      ref={ref}
      data-border={border}
      data-border-color={borderColor}
      className={clsx(
        'group',
        selectTrigger({
          border,
          borderColor,
          backgroundColor: disabled ? 'disabled' : 'enabled',
        }),
        className,
      )}
      disabled={disabled}
      {...props}
    >
      {children}
    </Trigger>
  );
};

const SelectItem = function SelectItem({
  ref,
  children,
  className,
  ...props
}: SelectItemProps & { ref?: React.Ref<HTMLDivElement> }) {
  return (
    <Item
      ref={ref}
      className={clsx(
        'text-s rounded-xs p-sm font-medium outline-hidden',
        'radix-highlighted:bg-purple-background-light radix-highlighted:text-purple-primary',
        className,
      )}
      {...props}
    >
      {children}
    </Item>
  );
};

const SelectValue = function SelectValue({
  ref,
  className,
  align = 'center',
  ...props
}: SelectValueProps & { align?: 'center' | 'start'; ref?: React.Ref<HTMLDivElement> }) {
  return (
    <span
      className={clsx(
        'w-full group-data-[border=rounded]/trigger:px-xs',
        { 'text-center': align === 'center', 'text-start': align === 'start' },
        className,
      )}
    >
      <Value ref={ref} {...props} />
    </span>
  );
};

const SelectArrow = () => (
  <SelectIcon className="group-radix-state-open:rotate-180 text-grey-primary size-6 shrink-0" asChild>
    <Icon icon="arrow-2-down" />
  </SelectIcon>
);

export type SelectProps = RawSelectProps &
  Pick<SelectValueProps, 'placeholder'> &
  Pick<SelectTriggerProps, 'border' | 'borderColor' | 'className'>;

const SelectDefault = function SelectDefault({
  ref: triggerRef,
  children,
  placeholder,
  border,
  borderColor,
  className,
  ...props
}: SelectProps & { ref?: React.Ref<HTMLButtonElement> }) {
  return (
    <Root {...props}>
      <Select.Trigger ref={triggerRef} border={border} borderColor={borderColor} className={className}>
        <Select.Value placeholder={placeholder} />
        <Select.Arrow />
      </Select.Trigger>
      <Select.Content
        className="z-50 max-h-60 min-w-(--radix-select-trigger-width)"
        align={border === 'rounded' ? 'center' : 'start'}
      >
        <Select.Viewport>{children}</Select.Viewport>
      </Select.Content>
    </Root>
  );
};

const SelectDefaultItem = function SelectDefaultItem({
  ref,
  children,
  className,
  ...props
}: SelectItemProps & { ref?: React.Ref<HTMLDivElement> }) {
  return (
    <SelectItem ref={ref} className={clsx('min-h-10', className)} {...props}>
      <Select.ItemText>{children}</Select.ItemText>
    </SelectItem>
  );
};
/**
 * @deprecated This Select component is deprecated and will be removed in a future release.
 * Please migrate to the new MenuCommand component as soon as possible.
 */
export const Select = {
  Default: SelectDefault,
  Root,
  Trigger: SelectTrigger,
  Arrow: SelectArrow,
  Content: SelectContent,
  Viewport: SelectViewport,
  Item: SelectItem,
  DefaultItem: SelectDefaultItem,
  ItemText,
  Value: SelectValue,
};

export type SelectOption<T> = {
  label: ReactNode | (() => ReactNode);
  value: T;
  rowValue?: string;
  disabled?: boolean;
};

type SelectV2BaseProps<T, O extends SelectOption<T>> = {
  placeholder: string;
  options: O[];
  size?: 'small' | 'medium' | 'large';
  'aria-label'?: string;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  displayedValue?: (option: O) => ReactNode;
  selectedIcon?: IconName;
  variant?: 'tag' | 'default';
  menuClassName?: string;
};

type SelectV2SingleProps<T, O extends SelectOption<T>> = SelectV2BaseProps<T, O> & {
  multiple?: false;
  value: T;
  onChange: (value: T) => void;
};

type SelectV2MultipleProps<T, O extends SelectOption<T>> = SelectV2BaseProps<T, O> & {
  multiple: true;
  value: T[];
  onChange: (value: T[]) => void;
};

export type SelectV2Props<T, O extends SelectOption<T> = SelectOption<T>> =
  | SelectV2SingleProps<T, O>
  | SelectV2MultipleProps<T, O>;

function renderOptionLabel<T, O extends SelectOption<T>>(
  option: O,
  displayedValue?: (option: O) => ReactNode,
): ReactNode {
  if (displayedValue) return displayedValue(option);
  return typeof option.label === 'function' ? option.label() : option.label;
}

function getNodeText(node: ReactNode): string | null {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) {
    const text = node.map(getNodeText).filter(Boolean).join('');
    return text.length > 0 ? text : null;
  }
  if (isValidElement<{ children?: ReactNode }>(node)) return getNodeText(node.props.children);
  return null;
}

export function SelectV2<T, O extends SelectOption<T> = SelectOption<T>>(
  props: SelectV2Props<T, O> & { ref?: React.Ref<HTMLButtonElement> },
) {
  const {
    ref,
    size,
    options,
    placeholder,
    disabled,
    readOnly,
    className,
    displayedValue,
    selectedIcon,
    variant = 'default',
    menuClassName,
    'aria-label': ariaLabel,
  } = props;

  const [open, setOpen] = useState(false);
  const justSelectedRef = useRef(false);

  const handleOpenChange = (newOpen: boolean) => {
    if (props.multiple && !newOpen && justSelectedRef.current) {
      justSelectedRef.current = false;
      return;
    }
    setOpen(newOpen);
  };

  const isSelected = (optionValue: T): boolean => {
    if (props.multiple) return props.value.some((v) => v === optionValue);
    return props.value === optionValue;
  };

  const handleSelect = (optionValue: T) => {
    if (props.multiple) {
      justSelectedRef.current = true;
      const current = props.value;
      const next = current.some((v) => v === optionValue)
        ? current.filter((v) => v !== optionValue)
        : [...current, optionValue];
      props.onChange(next);
    } else {
      props.onChange(optionValue);
    }
  };

  const selectedOptions = props.multiple ? options.filter((o) => props.value.some((v) => v === o.value)) : null;

  const singleValueLabel = !props.multiple
    ? (() => {
        const currentOption = options.find((o) => o.value === props.value);
        return currentOption ? renderOptionLabel(currentOption, displayedValue) : placeholder;
      })()
    : null;

  const isPlaceholderShown = props.multiple ? props.value.length === 0 : isNullish(props.value);
  const renderTriggerContent = () => {
    if (props.multiple && selectedOptions && selectedOptions.length > 0) {
      return (
        <ExpandableGroupTagLine
          classname="gap-xs"
          overflowBehavior="popover"
          items={selectedOptions.map((opt, i) => (
            <Tag key={i} color="grey" size="small" className="max-w-full">
              {renderOptionLabel(opt, displayedValue)}
            </Tag>
          ))}
        />
      );
    }
    return <span>{singleValueLabel ?? placeholder}</span>;
  };

  const triggerAccessibleName =
    ariaLabel ??
    (props.multiple
      ? (selectedOptions
          ?.map((option) => getNodeText(renderOptionLabel(option, displayedValue)))
          .filter(Boolean)
          .join(', ') ??
          '') ||
        placeholder
      : (getNodeText(singleValueLabel) ?? placeholder));

  return (
    <MenuCommand.Menu open={open} onOpenChange={handleOpenChange}>
      <MenuCommand.Trigger>
        {variant === 'default' ? (
          <MenuCommand.SelectButton
            size={size}
            role="combobox"
            aria-label={triggerAccessibleName}
            ref={ref}
            disabled={disabled}
            readOnly={readOnly}
            className={cn(props.multiple && 'min-w-0 overflow-hidden', className)}
            {...(isPlaceholderShown ? { 'data-placeholder-shown': true } : {})}
          >
            {renderTriggerContent()}
          </MenuCommand.SelectButton>
        ) : (
          <button
            ref={ref}
            aria-label={triggerAccessibleName}
            disabled={disabled}
            className={cn('flex gap-2xs items-center', className)}
          >
            {props.multiple && selectedOptions && selectedOptions.length > 0 ? (
              <Tag color="purple">
                <span className="flex items-center">
                  {selectedOptions.map((opt, i) => (
                    <span key={i}>
                      {i > 0 ? ', ' : ''}
                      {renderOptionLabel(opt, displayedValue)}
                    </span>
                  ))}
                </span>
                <Icon icon="caret-down" className="size-4" />
              </Tag>
            ) : (
              <Tag color="purple">
                <span>{singleValueLabel ?? placeholder}</span>
                <Icon icon="caret-down" className="size-4" />
              </Tag>
            )}
          </button>
        )}
      </MenuCommand.Trigger>
      <MenuCommand.Content
        align="start"
        sameWidth
        sideOffset={4}
        size={variant === 'tag' ? 'small' : undefined}
        className={menuClassName}
      >
        <MenuCommand.List>
          {options.map((option, idx) => {
            const isPrimitiveValue = ['number', 'string', 'bool'].includes(typeof option.value);
            const itemValue =
              option.rowValue !== undefined ? option.rowValue : isPrimitiveValue ? String(option.value) : undefined;

            return (
              <MenuCommand.Item
                role="option"
                key={idx}
                disabled={option.disabled}
                onSelect={() => handleSelect(option.value)}
                className="group-[[data-size='small']]/menu-command-content:h-6"
                value={itemValue}
              >
                <span>{typeof option.label === 'function' ? option.label() : option.label}</span>
                {isSelected(option.value) && (
                  <Icon icon={selectedIcon ?? 'tick'} className="size-5 text-purple-primary" />
                )}
              </MenuCommand.Item>
            );
          })}
        </MenuCommand.List>
      </MenuCommand.Content>
    </MenuCommand.Menu>
  );
}
